import { useI18n } from '@/context/I18nContext';
import { useAuth } from '@/context/AuthContext';
import { getGoogleClientId, getAppleClientId } from '@/lib/config';
import { redirectToGoogleAuth, redirectToAppleAuth } from './oauth';
import { LanguageDropdown } from './LanguageDropdown';
import './login.css';

/** Google "G" logo as inline SVG — follows Google branding guidelines. */
export function GoogleLogo({ size = 18 }: { size?: number }) {
  return (
    <svg viewBox="0 0 48 48" width={size} height={size}>
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59A14.5 14.5 0 019.5 24c0-1.59.28-3.14.76-4.59l-7.98-6.19A23.99 23.99 0 000 24c0 3.77.9 7.35 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}

/** Apple logo as inline SVG — follows Apple branding guidelines. */
export function AppleLogo({ size = 18 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 6.37c.61-.75 1.04-1.8.92-2.85-.9.04-2 .6-2.64 1.35-.56.65-.99 1.7-.87 2.71 1.01.08 1.98-.46 2.59-1.21z" />
    </svg>
  );
}

export function useOAuthSignIn(nativeAppleClientId?: string) {
  const { handleCredentialResponse } = useAuth();

  const handleGoogleSignIn = async () => {
    const isNative = typeof (window as any).Capacitor !== 'undefined' && (window as any).Capacitor?.isNativePlatform?.();
    if (isNative) {
      const GoogleSignIn = (window as any).__NATIVE_AUTH__?.GoogleSignIn;
      if (GoogleSignIn?.signIn) {
        try {
          try {
            await GoogleSignIn.initialize({
              clientId: '741273044106-h5a1civdk4vbj0un9gm32burtlgl1plg.apps.googleusercontent.com',
            });
          } catch (initErr) {
            console.error('GoogleSignIn init error:', initErr);
          }
          const result = await GoogleSignIn.signIn();
          const idToken = result?.idToken;
          if (idToken) {
            await handleCredentialResponse({ credential: idToken });
          }
        } catch (err: any) {
          console.error('Native Google Sign-In error:', err);
        }
      }
      return;
    }

    const clientId = getGoogleClientId();
    if (!clientId) {
      console.error('Google Client ID not configured');
      return;
    }
    const returnPath = window.location.hash || '#/';
    redirectToGoogleAuth(clientId, returnPath);
  };

  const handleAppleSignIn = async () => {
    const isNative = typeof (window as any).Capacitor !== 'undefined' && (window as any).Capacitor?.isNativePlatform?.();
    if (isNative) {
      const SignInWithApple = (window as any).__NATIVE_AUTH__?.SignInWithApple;
      if (SignInWithApple?.authorize) {
        try {
          const result = await SignInWithApple.authorize({
            clientId: nativeAppleClientId || (window as any).APP_BUNDLE_ID || 'com.yanicksenn.telemetry.extractor',
            redirectURI: '',
            scopes: 'email name',
            state: '12345',
            nonce: 'nonce',
          });
          const resp = result?.response;
          const idToken = resp?.identityToken;
          if (idToken) {
            const nameParts = [resp?.givenName, resp?.familyName]
              .map((part) => (typeof part === 'string' ? part.trim() : ''))
              .filter(Boolean);
            const name = nameParts.length > 0 ? nameParts.join(' ') : undefined;
            await handleCredentialResponse({ credential: idToken, name });
          }
        } catch (err: any) {
          console.error('Native Apple Sign-In error:', err);
        }
      }
      return;
    }

    const clientId = getAppleClientId();
    if (!clientId) {
      console.error('Apple Client ID not configured');
      return;
    }
    const returnPath = window.location.hash || '#/';
    redirectToAppleAuth(clientId, returnPath);
  };

  return { handleGoogleSignIn, handleAppleSignIn };
}

interface LoginPageProps {
  /** The application title displayed on the login page (e.g. "Telemetry", "Editor"). */
  title: string;
  /** Optional native Apple client ID / bundle ID override. Defaults to com.yanicksenn.telemetry.extractor. */
  nativeAppleClientId?: string;
  /** Whether to show the manual language selector. Defaults to true when setLocale is available. */
  showLanguageSelector?: boolean;
}

export function LoginPage({ title, nativeAppleClientId, showLanguageSelector = true }: LoginPageProps) {
  const { t, locale, setLocale } = useI18n();
  const { handleGoogleSignIn, handleAppleSignIn } = useOAuthSignIn(nativeAppleClientId);

  return (
    <div className="login-page">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="login-bg-grid" />
        <div className="login-bg-glow" />
      </div>

      <div className="relative z-10 w-full max-w-sm px-5 login-fade-in">
        {/* Logo & branding */}
        <div className="text-center mb-8">
          <div className="login-logo-ring">
            <svg viewBox="0 0 40 40" fill="none" className="w-10 h-10">
              <path d="M20 4 L36 12 L36 28 L20 36 L4 28 L4 12 Z" stroke="var(--color-accent)" strokeWidth="1.5" fill="none" opacity="0.4" />
              <circle cx="20" cy="20" r="6" fill="var(--color-accent)" opacity="0.9" />
              <circle cx="20" cy="20" r="3" fill="var(--color-bg-base)" />
              {/* Tachometer-like tick marks */}
              <line x1="20" y1="8" x2="20" y2="11" stroke="var(--color-accent)" strokeWidth="1.2" opacity="0.6" />
              <line x1="28.5" y1="11" x2="27" y2="13.5" stroke="var(--color-accent)" strokeWidth="1.2" opacity="0.6" />
              <line x1="32" y1="20" x2="29" y2="20" stroke="var(--color-accent)" strokeWidth="1.2" opacity="0.6" />
              <line x1="11.5" y1="11" x2="13" y2="13.5" stroke="var(--color-accent)" strokeWidth="1.2" opacity="0.6" />
              <line x1="8" y1="20" x2="11" y2="20" stroke="var(--color-accent)" strokeWidth="1.2" opacity="0.6" />
            </svg>
          </div>

          <h1 className="text-[1.75rem] font-extrabold tracking-tight text-text-primary mt-5">
            {title}
          </h1>
          <p className="text-sm text-text-muted mt-1.5 tracking-wide uppercase font-medium">
            {t('auth.subtitle')}
          </p>
        </div>

        {/* Sign-in provider buttons */}
        <div className="flex flex-col items-center gap-3 w-full max-w-xs mx-auto">
          <button onClick={handleGoogleSignIn} className="login-provider-btn w-full">
            <GoogleLogo size={18} />
            <span>{t('auth.signInWithGoogle')}</span>
          </button>

          <button onClick={handleAppleSignIn} className="login-provider-btn w-full">
            <AppleLogo size={18} />
            <span>{t('auth.signInWithApple')}</span>
          </button>
        </div>

        {/* Language selector */}
        {showLanguageSelector && setLocale && (
          <div className="mt-8 flex justify-center">
            <LanguageDropdown
              locale={locale}
              onChange={setLocale}
              align="center"
              direction="up"
              testIdPrefix="login-locale"
            />
          </div>
        )}
      </div>
    </div>
  );
}
